import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { trialService } from '../services/membership/trialService'; // ADDED FOR TOKEN SYSTEM
import { tokenService } from '../services/membership/tokenService'; // ADDED FOR TOKEN SYSTEM

interface OnboardingContextType {
  step: number;
  totalSteps: number;
  goals: string[];
  skillLevel: number;
  mentorId: string | null;
  isComplete: boolean;
  setStep: (step: number) => void;
  next: () => void;
  prev: () => void;
  toggleGoal: (id: string) => void;
  setSkillLevel: (level: number) => void;
  setMentor: (id: string) => void;
  complete: () => void;
}

const TOTAL_STEPS = 4;

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [step, setStep] = useState(0);
  const [goals, setGoals] = useState<string[]>([]);
  const [skillLevel, setSkillLevel] = useState(50);
  const [mentorId, setMentorId] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);

  // ADDED FOR TOKEN SYSTEM: activate the 21-day free trial (and grant its 60
  // starter tokens) the first time a new user reaches onboarding.
  useEffect(() => {
    if (trialService.isEligible()) {
      void trialService.startTrial();
    }
  }, []);

  const value: OnboardingContextType = {
    step,
    totalSteps: TOTAL_STEPS,
    goals,
    skillLevel,
    mentorId,
    isComplete,
    setStep,
    next: () => setStep((s) => Math.min(TOTAL_STEPS, s + 1)),
    prev: () => setStep((s) => Math.max(0, s - 1)),
    toggleGoal: (id) =>
      setGoals((prev) => (prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id])),
    setSkillLevel,
    setMentor: setMentorId,
    // ADDED FOR TOKEN SYSTEM: award a one-time completion bonus.
    complete: () =>
      setIsComplete((prev) => {
        if (!prev) void tokenService.earn(20, 'onboarding', 'Completed onboarding setup', 90);
        return true;
      }),
  };

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

export function useOnboardingContext() {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboardingContext must be used within an OnboardingProvider');
  }
  return context;
}
